import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Switch,
  Tabs,
  Tab,
  Stack,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import PreviewIcon from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import { toast } from 'react-hot-toast';
import printSettingsService from '../../services/printSettingsService';

const PAGE_BG = '#f4f6f8';
const PANEL_BORDER = '1px solid #e5e7eb';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`print-settings-tabpanel-${index}`}>
      {value === index ? children : null}
    </div>
  );
}

function SettingRow({ label, warning, checked, onChange, disabled, loading }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: PANEL_BORDER,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ flex: 1, pr: 2, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {warning ? (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25 }}>
            {warning}
          </Typography>
        ) : null}
      </Box>
      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Switch
          size="small"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          color="primary"
        />
      )}
    </Box>
  );
}

const PrintSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [selectedPaperSize, setSelectedPaperSize] = useState('A4'); // First level: Paper size
  const [selectedFormat, setSelectedFormat] = useState('A4'); // Second level: Actual format (e.g., A4, 3_INCH_SIMPLE, 3_INCH_GST_DETAILS)
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [enableWhatsappShare, setEnableWhatsappShare] = useState(false);
  const [updatingWhatsappShare, setUpdatingWhatsappShare] = useState(false);
  const [directPrint, setDirectPrint] = useState(false);
  const [updatingDirectPrint, setUpdatingDirectPrint] = useState(false);
  const [previewPrint, setPreviewPrint] = useState(false);
  const [updatingPreviewPrint, setUpdatingPreviewPrint] = useState(false);
  const [showPrintSchemeSelection, setShowPrintSchemeSelection] = useState(false);
  const [updatingPrintSchemeSelection, setUpdatingPrintSchemeSelection] = useState(false);
  const [showPrintNameInBill, setShowPrintNameInBill] = useState(false);
  const [updatingPrintNameInBill, setUpdatingPrintNameInBill] = useState(false);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [electronPrinters, setElectronPrinters] = useState([]);
  const [selectedElectronPrinter, setSelectedElectronPrinter] = useState('');
  const [selectedLabelPrinter, setSelectedLabelPrinter] = useState('');
  const [labelDpi, setLabelDpi] = useState(203);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [savingElectronPrinter, setSavingElectronPrinter] = useState(false);
  const [savingLabelPrinter, setSavingLabelPrinter] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [savedFormat, setSavedFormat] = useState('A4');
  const [isElectron, setIsElectron] = useState(false);

  const formatHasChanges = selectedFormat !== savedFormat;

  const whatsappFeatureEnabled =
    !planFeatures ||
    planFeatures._treatAllEnabled ||
    planFeatures.whatsapp_bill_share === true ||
    (typeof planFeatures.whatsapp_bill_share === 'string' &&
      planFeatures.whatsapp_bill_share.toLowerCase() === 'true');

  // Format options by paper size
  const formatOptions = {
    'A5': [
      { value: 'A5_FORMAT_1', label: 'A5 Format 1 (Landscape Invoice)' },
      { value: 'A5_FORMAT_2_DOUBLE', label: 'A5 Format 2 Double (2 copies on A5)' },
      { value: 'A5_FORMAT_2_DOUBLE_GST', label: 'A5 Format 3 Double With GST (2 copies on A5)'},
      { value: 'A5_FORMAT_4_PORTRAIT', label: 'A5 Format 4 Portrait (Tax Invoice)'}
    ],
    'A4': [
      { value: 'A4', label: 'A4 Standard' },
      { value: 'A4_FORMAT_2', label: 'A4 Format 2 (Detailed GST)' },
      { value: 'A4_FORMAT_3', label: 'A4 Format 3 (Boxed Professional)' },
      { value: 'A4_FORMAT_4', label: 'A4 Format 4 (Legacy)' },
      { value: 'A4_FORMAT_4_PORTRAIT', label: 'A4 Format 4 Portrait (Tax Invoice)' }
    ],
    'A6': [
      { value: 'A6_FORMAT_1', label: 'A6 Format 1 (Bill details only)' }
    ],
    '3_INCH': [
      { value: '3_INCH_SIMPLE', label: '3-inch (Simple)' },
      { value: '3_INCH_GST_DETAILS', label: '3-inch (GST Details)' }
    ],
    '2_INCH': [
      { value: '2_INCH_SIMPLE', label: '2-inch (Standard)' }
    ]
  };

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  // Load current print settings (including WhatsApp share)
  useEffect(() => {
    loadPrintSettings();
    if (window.electronAPI) {
      loadElectronPrinters();
    }
    setIsElectron(!!window.electronAPI);
  }, []);

  const loadElectronPrinters = async () => {
    try {
      setLoadingPrinters(true);
      const printers = await window.electronAPI.getPrinters();
      setElectronPrinters(printers || []);
      if (window.electronAPI.getPrinterConfig) {
        const cfg = await window.electronAPI.getPrinterConfig();
        setSelectedElectronPrinter(cfg?.defaultPrinter || '');
        setSelectedLabelPrinter(cfg?.labelPrinter || '');
        setLabelDpi(cfg?.labelDpi === 300 ? 300 : 203);
      } else {
        const defaultName = await window.electronAPI.getDefaultPrinter();
        setSelectedElectronPrinter(defaultName || '');
      }
    } catch (e) {
      console.error('Failed to load electron printers:', e);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handleSaveElectronPrinter = async () => {
    try {
      setSavingElectronPrinter(true);
      await window.electronAPI.setDefaultPrinter(selectedElectronPrinter);
      toast.success('Desktop printer setting saved locally');
    } catch (e) {
      console.error('Error saving electron printer:', e);
      toast.error('Failed to save printer setting');
    } finally {
      setSavingElectronPrinter(false);
    }
  };

  const handleSaveLabelPrinter = async () => {
    if (!window.electronAPI?.setPrinterConfig) return;
    try {
      setSavingLabelPrinter(true);
      await window.electronAPI.setPrinterConfig({
        labelPrinter: selectedLabelPrinter || null,
        labelDpi
      });
      toast.success('Label printer setting saved locally');
    } catch (e) {
      console.error('Error saving label printer:', e);
      toast.error('Failed to save label printer');
    } finally {
      setSavingLabelPrinter(false);
    }
  };

  // Reactively track plan features for WhatsApp Share visibility (same as CompanySettings / Navigation)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__PLAN_FEATURES__) {
      setPlanFeatures(window.__PLAN_FEATURES__);
    }
    const handlePlanFeaturesUpdated = (event) => {
      const next = event?.detail ?? (typeof window !== 'undefined' && window.__PLAN_FEATURES__) ?? null;
      setPlanFeatures(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
      }
    };
  }, []);

  // Handle Escape key to close preview dialog
  useEffect(() => {
    if (!previewOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClosePreview();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [previewOpen, handleClosePreview]);

  const getLocationId = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.location?.id;
      } catch (e) {
        console.error('Error parsing user from localStorage', e);
      }
    }
    return null;
  };

  const loadPrintSettings = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const locationId = getLocationId();
      const response = await printSettingsService.getPrintSettings(locationId);
      if (response.success) {
        // Handle print format
        if (response.printFormat) {
          let format = response.printFormat.toUpperCase();
          if (format === '3_INCH') {
            format = '3_INCH_SIMPLE'; // Migrate old format to new name
          }
          // Custom format keys are for printing only; map to base paper family for this picker
          if (format.startsWith('CUSTOM_')) {
            if (format.includes('3INCHGST') || format.includes('3_INCH_GST')) {
              format = '3_INCH_GST_DETAILS';
            } else if (format.includes('3INCH') || format.includes('3_INCH')) {
              format = '3_INCH_SIMPLE';
            } else if (format.includes('2INCH') || format.includes('2_INCH')) {
              format = '2_INCH_SIMPLE';
            } else if (format.includes('A5')) {
              format = 'A5_FORMAT_1';
            } else if (format.includes('A6')) {
              format = 'A6_FORMAT_1';
            } else {
              format = 'A4';
            }
          }
          setSelectedFormat(format);
          setSavedFormat(format);

          // Determine paper size based on format
          if (format.startsWith('3_INCH')) {
            setSelectedPaperSize('3_INCH');
          } else if (format.startsWith('2_INCH')) {
            setSelectedPaperSize('2_INCH');
          } else if (format.startsWith('A6')) {
            setSelectedPaperSize('A6');
          } else if (format.startsWith('A5')) {
            setSelectedPaperSize('A5');
          } else {
            setSelectedPaperSize('A4');
          }
        } else {
          // Default to A4 if not set
          setSelectedFormat('A4');
          setSelectedPaperSize('A4');
          setSavedFormat('A4');
        }

        // Handle WhatsApp share
        if (typeof response.enableWhatsappShare === 'boolean') {
          setEnableWhatsappShare(response.enableWhatsappShare);
        }

        // Handle Direct Print
        if (typeof response.directPrint === 'boolean') {
          setDirectPrint(response.directPrint);
        }

        // Handle Preview & Print
        if (typeof response.previewPrint === 'boolean') {
          setPreviewPrint(response.previewPrint);
        }

        // Handle Print Scheme Selection
        if (typeof response.showPrintSchemeSelection === 'boolean') {
          setShowPrintSchemeSelection(response.showPrintSchemeSelection);
        }
        if (typeof response.showPrintNameInBill === 'boolean') {
          setShowPrintNameInBill(response.showPrintNameInBill);
        }
      } else {
        setSelectedFormat('A4');
        setSelectedPaperSize('A4');
        setSavedFormat('A4');
      }
    } catch (error) {
      console.error('Error loading print settings:', error);
      setErrorMessage('Failed to load print settings');
      toast.error('Failed to load print settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');

      const locationId = getLocationId();
      const response = await printSettingsService.updatePrintSettings(selectedFormat, locationId);

      if (response.success) {
        setSavedFormat(selectedFormat);
        setSuccessMessage(
          'Print format updated. Built-in templates (e.g. 3-inch GST Details, A5) are now used for printing. ' +
          'Any previous custom layout was turned off — use Customize again and activate when you need it.'
        );
        toast.success('Print format updated — using built-in layout for printing');
      } else {
        setErrorMessage(response.message || 'Failed to update print format');
        toast.error(response.message || 'Failed to update print format');
      }
    } catch (error) {
      console.error('Error updating print settings:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update print format';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handlePaperSizeChange = (paperSize) => {
    setSelectedPaperSize(paperSize);
    // Auto-select first format of the selected paper size
    if (formatOptions[paperSize] && formatOptions[paperSize].length > 0) {
      setSelectedFormat(formatOptions[paperSize][0].value);
    }
  };

  const handlePreview = async (format) => {
    try {
      setPreviewing(true);
      setErrorMessage('');

      const pdfBlob = await printSettingsService.previewPrintFormat(format);

      // Create URL for the blob
      const url = window.URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
      setPreviewOpen(true);

      toast.success('Preview generated');
    } catch (error) {
      console.error('Error generating preview:', error);
      const errorMsg = error.response?.data?.message || 'Failed to generate preview';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPreviewing(false);
    }
  };

  const handleWhatsappShareToggle = async (newValue) => {
    try {
      setUpdatingWhatsappShare(true);

      // Validation: Can't enable WhatsApp share if directPrint is enabled
      if (newValue && directPrint) {
        toast.error('Cannot enable WhatsApp Share when Direct Print is enabled. Please disable Direct Print first.');
        setUpdatingWhatsappShare(false);
        return;
      }

      // Update local state immediately for better UX
      setEnableWhatsappShare(newValue);

      // Update via print settings service (handles location-specific settings)
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ enableWhatsappShare: newValue }, locationId);

      toast.success(`WhatsApp share ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating WhatsApp share setting:', error);
      // Revert local state on error
      setEnableWhatsappShare(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update WhatsApp share setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingWhatsappShare(false);
    }
  };

  const handleDirectPrintToggle = async (newValue) => {
    try {
      setUpdatingDirectPrint(true);

      // Validation: Can't enable directPrint if previewPrint or enableWhatsappShare is enabled
      if (newValue) {
        if (previewPrint) {
          toast.error('Cannot enable Direct Print when Preview & Print is enabled. Please disable Preview & Print first.');
          setUpdatingDirectPrint(false);
          return;
        }
        if (enableWhatsappShare) {
          toast.error('Cannot enable Direct Print when WhatsApp Share is enabled. Please disable WhatsApp Share first.');
          setUpdatingDirectPrint(false);
          return;
        }
      }

      // Update local state immediately for better UX
      setDirectPrint(newValue);

      // Update via print settings service
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ directPrint: newValue }, locationId);

      toast.success(`Direct Print ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Direct Print setting:', error);
      // Revert local state on error
      setDirectPrint(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Direct Print setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingDirectPrint(false);
    }
  };

  const handlePreviewPrintToggle = async (newValue) => {
    try {
      setUpdatingPreviewPrint(true);

      // Validation: Can't enable previewPrint if directPrint is enabled
      if (newValue && directPrint) {
        toast.error('Cannot enable Preview & Print when Direct Print is enabled. Please disable Direct Print first.');
        setUpdatingPreviewPrint(false);
        return;
      }

      // Update local state immediately for better UX
      setPreviewPrint(newValue);

      // Update via print settings service
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ previewPrint: newValue }, locationId);

      toast.success(`Preview & Print ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Preview & Print setting:', error);
      // Revert local state on error
      setPreviewPrint(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Preview & Print setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingPreviewPrint(false);
    }
  };

  const handlePrintSchemeSelectionToggle = async (newValue) => {
    try {
      setUpdatingPrintSchemeSelection(true);

      // Validation: Can't enable if directPrint is enabled
      if (newValue && directPrint) {
        toast.error('Cannot enable Print Scheme Selection when Direct Print is enabled.');
        setUpdatingPrintSchemeSelection(false);
        return;
      }

      // Update local state immediately for better UX
      setShowPrintSchemeSelection(newValue);

      // Update via print settings service
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ showPrintSchemeSelection: newValue }, locationId);

      toast.success(`Print Scheme Selection ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Print Scheme Selection setting:', error);
      // Revert local state on error
      setShowPrintSchemeSelection(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Print Scheme Selection setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingPrintSchemeSelection(false);
    }
  };

  const handlePrintNameInBillToggle = async (newValue) => {
    try {
      setUpdatingPrintNameInBill(true);
      setShowPrintNameInBill(newValue);
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ showPrintNameInBill: newValue }, locationId);
      toast.success(`Print Name in Bill ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Print Name in Bill setting:', error);
      setShowPrintNameInBill(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Print Name in Bill setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingPrintNameInBill(false);
    }
  };

  const handleFormatReset = () => {
    setSelectedFormat(savedFormat);
    if (savedFormat.startsWith('3_INCH')) {
      setSelectedPaperSize('3_INCH');
    } else if (savedFormat.startsWith('2_INCH')) {
      setSelectedPaperSize('2_INCH');
    } else if (savedFormat.startsWith('A6')) {
      setSelectedPaperSize('A6');
    } else if (savedFormat.startsWith('A5')) {
      setSelectedPaperSize('A5');
    } else {
      setSelectedPaperSize('A4');
    }
    setSuccessMessage('');
    setErrorMessage('');
  };

  const behaviorTabIndex = 1;
  const desktopTabIndex = isElectron ? 2 : null;

  const actionBar = activeTab === 0 ? (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      {formatHasChanges && (
        <Chip size="small" label="Unsaved" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
      )}
      <Button variant="outlined" size="small" onClick={handleFormatReset} disabled={!formatHasChanges || saving}>
        Reset
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={!formatHasChanges || saving || loading}
      >
        {saving ? 'Saving…' : 'Save format'}
      </Button>
    </Stack>
  ) : null;

  return (
    <Layout>
      <PageTitle title="Print Settings" />
      <Box sx={{ bgcolor: PAGE_BG, pb: 4 }}>
        <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                border: PANEL_BORDER,
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderBottom: PANEL_BORDER,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <PrintIcon sx={{ fontSize: 22, color: '#fff' }} />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Typography variant="h6" component="h1" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 }}>
                    Print settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {activeTab === 0
                      ? 'Choose paper size and receipt template, then save.'
                      : activeTab === behaviorTabIndex
                        ? 'Behavior toggles save automatically.'
                        : 'Desktop-only printer setup for this computer.'}
                  </Typography>
                </Box>
                {actionBar}
              </Box>

              {(successMessage || errorMessage) && (
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
                  {successMessage && (
                    <Alert severity="success" onClose={() => setSuccessMessage('')}>
                      {successMessage}
                    </Alert>
                  )}
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: successMessage ? 1 : 0 }} onClose={() => setErrorMessage('')}>
                      {errorMessage}
                    </Alert>
                  )}
                </Box>
              )}

              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: { xs: 1, sm: 2 },
                  borderBottom: PANEL_BORDER,
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    py: 1.25,
                    px: 2,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: 'text.secondary',
                    '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
                  },
                  '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                }}
              >
                <Tab label="Print format" id="print-settings-tab-0" aria-controls="print-settings-tabpanel-0" />
                <Tab label="Print behavior" id="print-settings-tab-1" aria-controls="print-settings-tabpanel-1" />
                {isElectron && (
                  <Tab label="Desktop printers" id="print-settings-tab-2" aria-controls="print-settings-tabpanel-2" />
                )}
              </Tabs>

              <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <TabPanel value={activeTab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="paper-size-label">Paper size</InputLabel>
                        <Select
                          labelId="paper-size-label"
                          id="paper-size-select"
                          value={selectedPaperSize}
                          label="Paper size"
                          onChange={(e) => handlePaperSizeChange(e.target.value)}
                        >
                          <MenuItem value="A4">A4 (210mm × 297mm)</MenuItem>
                          <MenuItem value="A5">A5 (210mm × 148mm)</MenuItem>
                          <MenuItem value="A6">A6 (105mm × 148mm)</MenuItem>
                          <MenuItem value="3_INCH">3-inch (80mm width)</MenuItem>
                          <MenuItem value="2_INCH">2-inch (58mm width)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, pt: { md: 1 } }}>
                        Pick the paper your printer uses, then choose a template below. Use Preview before saving if you are unsure.
                      </Typography>
                    </Grid>
                  </Grid>

                  {selectedPaperSize && formatOptions[selectedPaperSize] && (
                    <Box sx={{ mt: 2.5 }}>
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={selectedFormat}
                          onChange={(e) => setSelectedFormat(e.target.value)}
                        >
                          <Grid container spacing={1.5}>
                            {formatOptions[selectedPaperSize].map((format) => (
                              <Grid item xs={12} key={format.value}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.25,
                                    border: selectedFormat === format.value ? '2px solid' : '1px solid',
                                    borderColor: selectedFormat === format.value ? 'primary.main' : '#e2e8f0',
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    bgcolor: selectedFormat === format.value ? 'rgba(25, 118, 210, 0.06)' : '#fff',
                                    transition: 'border-color 0.15s, background-color 0.15s',
                                    '&:hover': { borderColor: 'primary.light', bgcolor: '#f8fafc' },
                                  }}
                                  onClick={() => setSelectedFormat(format.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setSelectedFormat(format.value);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                  aria-label={`Select ${format.label}`}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormControlLabel
                                      value={format.value}
                                      control={<Radio size="small" />}
                                      label={
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {format.label}
                                        </Typography>
                                      }
                                      sx={{ m: 0, flex: 1, minWidth: 0 }}
                                    />
                                    <Stack direction="row" spacing={0.75} flexShrink={0}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={previewing ? <CircularProgress size={14} /> : <PreviewIcon fontSize="small" />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePreview(format.value);
                                        }}
                                        disabled={previewing}
                                        sx={{ minWidth: 0, px: 1.25 }}
                                      >
                                        Preview
                                      </Button>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon fontSize="small" />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/settings/print/customize?baseFormat=${format.value}`);
                                        }}
                                        sx={{ minWidth: 0, px: 1.25 }}
                                      >
                                        Customize
                                      </Button>
                                    </Stack>
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={behaviorTabIndex}>
                  <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, px: 2, bgcolor: '#fafbfc' }}>
                    <SettingRow
                      label="Preview & Print"
                      warning={directPrint ? 'Disable Direct Print first' : null}
                      checked={previewPrint}
                      onChange={handlePreviewPrintToggle}
                      disabled={directPrint}
                      loading={updatingPreviewPrint}
                    />
                    <SettingRow
                      label="Direct Print"
                      warning={
                        previewPrint || enableWhatsappShare
                          ? 'Disable Preview & Print and WhatsApp Share first'
                          : null
                      }
                      checked={directPrint}
                      onChange={handleDirectPrintToggle}
                      disabled={previewPrint || enableWhatsappShare}
                      loading={updatingDirectPrint}
                    />
                    <SettingRow
                      label="Show Print Scheme Selection"
                      warning={directPrint ? 'Disable Direct Print first' : null}
                      checked={showPrintSchemeSelection}
                      onChange={handlePrintSchemeSelectionToggle}
                      disabled={directPrint}
                      loading={updatingPrintSchemeSelection}
                    />
                    <SettingRow
                      label="Print Name in Bill"
                      checked={showPrintNameInBill}
                      onChange={handlePrintNameInBillToggle}
                      loading={updatingPrintNameInBill}
                    />
                    {whatsappFeatureEnabled && (
                      <SettingRow
                        label="Enable WhatsApp Share"
                        warning={directPrint ? 'Disable Direct Print first' : null}
                        checked={enableWhatsappShare}
                        onChange={handleWhatsappShareToggle}
                        disabled={directPrint}
                        loading={updatingWhatsappShare}
                      />
                    )}
                  </Paper>
                </TabPanel>

                {isElectron && (
                  <TabPanel value={activeTab} index={desktopTabIndex}>
                    <Stack spacing={2.5}>
                      <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, p: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                          These settings apply only on this computer. Choose the printer used for Direct Print in the desktop app.
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={8}>
                            <FormControl fullWidth size="small">
                              <InputLabel id="electron-printer-label">Receipt printer</InputLabel>
                              <Select
                                labelId="electron-printer-label"
                                id="electron-printer-select"
                                value={selectedElectronPrinter}
                                label="Receipt printer"
                                onChange={(e) => setSelectedElectronPrinter(e.target.value)}
                                disabled={loadingPrinters}
                              >
                                <MenuItem value="">
                                  <em>Use Windows Default</em>
                                </MenuItem>
                                {electronPrinters.map((printer) => (
                                  <MenuItem key={printer.name} value={printer.name}>
                                    {printer.name} {printer.isDefault ? '(System Default)' : ''}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Button
                              variant="contained"
                              fullWidth
                              size="small"
                              startIcon={savingElectronPrinter ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                              onClick={handleSaveElectronPrinter}
                              disabled={loadingPrinters || savingElectronPrinter}
                            >
                              Save receipt printer
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>

                      <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, p: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Label printer (TSC / TSPL)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, lineHeight: 1.5 }}>
                          Optional. Used for direct raw label printing from the desktop app. If not set, labels use the browser print flow.
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={5}>
                            <FormControl fullWidth size="small">
                              <InputLabel id="label-printer-label">Label printer</InputLabel>
                              <Select
                                labelId="label-printer-label"
                                id="label-printer-select"
                                value={selectedLabelPrinter}
                                label="Label printer"
                                onChange={(e) => setSelectedLabelPrinter(e.target.value)}
                                disabled={loadingPrinters}
                              >
                                <MenuItem value="">
                                  <em>Not set (browser print for labels)</em>
                                </MenuItem>
                                {electronPrinters.map((printer) => (
                                  <MenuItem key={`label-${printer.name}`} value={printer.name}>
                                    {printer.name} {printer.isDefault ? '(System Default)' : ''}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel id="label-dpi-label">DPI</InputLabel>
                              <Select
                                labelId="label-dpi-label"
                                id="label-dpi-select"
                                value={labelDpi}
                                label="DPI"
                                onChange={(e) => setLabelDpi(Number(e.target.value))}
                                disabled={loadingPrinters}
                              >
                                <MenuItem value={203}>203</MenuItem>
                                <MenuItem value={300}>300</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Button
                              variant="contained"
                              fullWidth
                              size="small"
                              startIcon={savingLabelPrinter ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                              onClick={handleSaveLabelPrinter}
                              disabled={loadingPrinters || savingLabelPrinter}
                            >
                              Save label printer
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Stack>
                  </TabPanel>
                )}
              </Box>
            </Paper>
          )}

          <Dialog
            open={previewOpen}
            onClose={handleClosePreview}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                height: '90vh',
                maxHeight: '90vh',
              },
            }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">Print Format Preview</Typography>
              <IconButton edge="end" color="inherit" onClick={handleClosePreview} aria-label="close preview">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
              {previewUrl && (
                <iframe
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Print Format Preview"
                />
              )}
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
    </Layout>
  );
};

export default PrintSettingsPage;

